import { useMemo } from 'react';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import type { WorkspaceDoc } from '@/lib/sync/entity-types';

export interface CollectionDocSummary {
  id: string;
  title: string;
  kind: 'document' | 'canvas';
  updatedAt: string;
}

const DEFAULT_LIMIT = 5;

/**
 * Returns the most recently updated documents in a collection, filtered to
 * non-archived, sorted by updatedAt desc, capped at `limit`.
 *
 * Reads from the entityProjection that powers the rest of the library view —
 * no new fetches.
 */
export function useCollectionDocuments(
  collectionId: string,
  limit: number = DEFAULT_LIMIT,
): readonly CollectionDocSummary[] {
  const allDocs = useWorkspaceDocs();
  return useMemo(() => {
    return allDocs
      .filter((d: WorkspaceDoc) => !d.isArchived && d.collectionIds.includes(collectionId))
      .slice()
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      .slice(0, limit)
      .map((d) => ({
        id: d.id,
        title: d.title || 'Untitled',
        kind: d.kind,
        updatedAt: d.updatedAt,
      }));
  }, [allDocs, collectionId, limit]);
}
